import { getWeekNumber, getWeekYear } from '../common';
import { CalendarWeek } from '../models/CalendarWeek';

export class DateTime {
  today = new Date();
  targetDate = this.today;
  timeWindowFrom = new Date(this.targetDate.getFullYear(), 6, 1);
  timeWindowTo = new Date(this.targetDate.getFullYear() + 1, 6, 0);

  constructor() {
    this.setTargetDate(this.today);
  }

  setTargetDate(date: Date) {
    this.targetDate = date;
    this.timeWindowFrom = this.getTimeWindowFrom(date);
    this.timeWindowTo = this.getTimeWindowTo(date);
  }

  /**
   * Get calendar weeks between two dates
   * @param {Date} from Start date
   * @param {Date} to End date
   * @returns {CalendarWeek[]} Array of calendar weeks
   */
  getCalendarWeeks(from: Date = this.timeWindowFrom, to: Date = this.timeWindowTo): CalendarWeek[] {
    let cws: CalendarWeek[] = [];
    let weekStart = new Date(from);
    while (weekStart <= to) {
      const cw = getWeekNumber(weekStart);
      const year = getWeekYear(weekStart);
      cws.push(
        new CalendarWeek({
          key: 'cw_' + cw + '_' + year,
          cw: cw,
          year: year,
          cw_start: new Date(weekStart),
          cw_end: new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 4),
        })
      );

      weekStart.setDate(weekStart.getDate() + 7);
    }
    return cws;
  }

  isWeekendDay(date: Date): boolean {
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
  }

  getMondayInTheSameWeek(date: Date): Date {
    const result = new Date(date);
    result.setDate(result.getDate() - result.getDay() + 1);
    return result;
  }

  getFridayInTheSameWeek(date: Date): Date {
    const result = new Date(date);
    result.setDate(result.getDate() - result.getDay() + 5);
    return result;
  }

  getLastFridayOfMonth(date: Date): Date {
    const result = this.getFridayInTheSameWeek(date);
    if (result.getMonth() === date.getMonth()) return result;

    result.setDate(result.getDate() - 7);
    return result;
  }

  getMondayDate(date: Date): Date {
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(date.setDate(diff));
  }

  getFridayDate(date: Date): Date {
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 5);
    return new Date(date.setDate(diff));
  }

  getFirstMondayOfMonth(date: Date): Date {
    const result = this.getMondayInTheSameWeek(date);
    if (result.getMonth() === date.getMonth()) return result;

    result.setDate(result.getDate() + 7);
    return result;
  }

  getTimeWindowTo(date: Date, months = 7): Date {
    const lastDayOfNext6Months = new Date(date.getFullYear(), date.getMonth() + months, 0);
    return this.isWeekendDay(lastDayOfNext6Months)
      ? this.getLastFridayOfMonth(lastDayOfNext6Months)
      : this.getFridayInTheSameWeek(lastDayOfNext6Months);
  }

  getWorkingDaysCount(startDate: Date, endDate: Date): number {
    const start = Math.floor(startDate.getTime() / (1000 * 60 * 60 * 24)); // Convert start date to days since epoch
    const end = Math.floor(endDate.getTime() / (1000 * 60 * 60 * 24)); // Convert end date to days since epoch

    const totalDays = end - start + 1; // Total days in the interval
    const fullWeeks = Math.floor(totalDays / 7); // Number of full weeks
    const remainingDays = totalDays % 7; // Remaining days after full weeks

    // Calculate the number of weekends in the interval
    const startDayOfWeek = (startDate.getDay() + 6) % 7; // Adjust to make Monday = 0, Sunday = 6
    const endDayOfWeek = (startDayOfWeek + remainingDays - 1) % 7;

    const weekendDays =
      fullWeeks * 2 +
      (remainingDays > 0 && startDayOfWeek + remainingDays > 5 ? 2 : 0) -
      (startDayOfWeek === 6 ? 1 : 0) -
      (endDayOfWeek === 5 ? 1 : 0);

    return totalDays - weekendDays;
  }

  getTimeWindowFrom(date: Date, months = 1): Date {
    const firstDayOfLastMonth = new Date(date.getFullYear(), date.getMonth() - months, 1);
    return this.isWeekendDay(firstDayOfLastMonth)
      ? this.getFirstMondayOfMonth(firstDayOfLastMonth)
      : this.getMondayInTheSameWeek(firstDayOfLastMonth);
  }
}

// Export a singleton instance
const dateTime = new DateTime();

export default dateTime;
